import { IconChevronDown16, IconChevronLeft16 } from '@warp-ds/icons/react';
import { Button, Toggle } from '@warp-ds/react';
import cn from 'classnames';
import type { PrepackageDestination } from '../../types';
import {
    type GeoHierarchyNode,
    nodeHasAllDescendantsSelected,
    nodeHasSelectedDescendant,
    nodeToDestination,
    sortedByName,
} from '../utils/destinationPicker';

type DestinationPickerDrilldownViewProps = {
    isMobile?: boolean;
    country: GeoHierarchyNode;
    expandedRegion?: string;
    selectedDestinations: PrepackageDestination[];
    isDestinationSelected: (destination: PrepackageDestination) => boolean;
    isMaxedOut: boolean;
    onBack: () => void;
    onExpandedRegionChange: (region?: string) => void;
    onToggleDestination: (destination: PrepackageDestination) => void;
};

export const DestinationPickerDrilldownView = ({
    isMobile = false,
    country,
    expandedRegion,
    selectedDestinations,
    isDestinationSelected,
    isMaxedOut,
    onBack,
    onExpandedRegionChange,
    onToggleDestination,
}: DestinationPickerDrilldownViewProps) => {
    const countryDestination = nodeToDestination(country, country.displayName || country.name);
    const countryName = country.displayName || country.name;
    const regions = sortedByName(country.children ?? []);

    const renderToggle = (destination: PrepackageDestination, label: React.ReactNode, parentNode?: GeoHierarchyNode) => {
        const isSelfSelected = isDestinationSelected(destination);
        const allDescendantsSelected = parentNode
            ? nodeHasAllDescendantsSelected({ node: parentNode, country: countryName, selected: selectedDestinations })
            : false;
        const someDescendantsSelected = parentNode
            ? nodeHasSelectedDescendant({ node: parentNode, country: countryName, selected: selectedDestinations })
            : false;

        const checked = isSelfSelected || allDescendantsSelected;
        const indeterminate = !checked && someDescendantsSelected;

        return (
            <Toggle
                type="checkbox"
                label={label}
                checked={checked}
                disabled={isMaxedOut && !checked}
                onChange={() => onToggleDestination(destination)}
                className="py-6"
                indeterminate={indeterminate}
            />
        );
    };

    return (
        <div>
            <div className={cn('flex items-center gap-8 border-b s-border', { 'py-10 px-0': isMobile, 'px-16 py-12': !isMobile })}>
                <Button
                    type="button"
                    utility
                    quiet
                    small
                    onClick={onBack}
                    aria-label="Tilbake"
                    className={cn('flex gap-8 items-center', { 'px-0': isMobile })}>
                    <IconChevronLeft16 aria-hidden />
                    <span className="font-bold text-m">Alle destinasjoner</span>
                </Button>
            </div>
            <div className={cn({ 'max-h-[360px] overflow-y-auto px-16 py-8': !isMobile, 'py-8': isMobile })}>
                <div className={cn('border-b s-border px-16 py-8', { 'px-4': !isMobile, 'px-16': isMobile })}>
                    {renderToggle(countryDestination, <span className="block font-bold">{country.name} (alle)</span>, country)}
                </div>
                <ul className={cn('list-none p-0', { 'mt-8': !isMobile })}>
                    {regions.map((region) => {
                        const regionDestination = nodeToDestination(region, countryName);
                        const destinations = sortedByName(region.children ?? []);
                        const isExpanded = expandedRegion === region.displayName;

                        if (destinations.length === 0) {
                            return (
                                <li key={region.displayName} className={cn('border-b s-border', { 'px-4': !isMobile, 'px-16': isMobile })}>
                                    {renderToggle(regionDestination, region.name)}
                                </li>
                            );
                        }

                        return (
                            <li key={region.displayName} className={cn('border-b s-border', { 'py-4': !isMobile })}>
                                <div className={cn('flex items-center gap-8 py-8', { 'px-4': !isMobile, 'px-16': isMobile })}>
                                    {renderToggle(regionDestination, region.name, region)}
                                    <button
                                        type="button"
                                        onClick={() => onExpandedRegionChange(isExpanded ? undefined : region.displayName)}
                                        aria-expanded={isExpanded}
                                        className="focusable flex flex-1 items-center justify-end gap-4 border-0 bg-transparent"
                                        aria-label={`${isExpanded ? 'Skjul' : 'Vis'} ${region.name}`}>
                                        <span className="mr-4 text-detail font-bold">Vis {destinations.length} reisemål</span>
                                        <IconChevronDown16
                                            aria-hidden
                                            className={cn('transition-transform', { 'rotate-180': isExpanded })}
                                        />
                                    </button>
                                </div>
                                {isExpanded && (
                                    <ul className={cn('list-none py-4', { 'pl-24 pr-4': !isMobile, 'pl-44 pr-16': isMobile })}>
                                        {destinations.map((destinationNode) => (
                                            <li key={destinationNode.displayName} className="py-8">
                                                {renderToggle(nodeToDestination(destinationNode, countryName), destinationNode.name)}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};
